import { faKey, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardInput from 'base/StandardInput/StandardInput';
import Button from 'components/base/Button';
import { t } from 'i18next';
import { useState } from 'react';
import { Form, Nav, Tab } from 'react-bootstrap';
import { LoginValidations } from 'utils/validations/ValidationsSet/LoginValidationSet';
import { LoginFormI } from '../../../../types/interface';
import validationFunctions from 'utils/validationUtils';
import { useAppDispatch } from '../../../redux/HooksRedux';
import * as authActions from '../../../redux/auth/authAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import { clearCookie, setCookie } from 'utils/cookieUtils';
import { ROLE, USER } from 'constant/RoutesUrls';
import { ACCESS_TOKEN, LogInType } from 'utils/enums/misc';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardLoader } from 'base/Loader/Loader';
import { capitalizeFirstLetter } from 'utils/helpers';
import { getProfile } from '../../../redux/userProfile/userProfileAction';
import { UilMicrosoft } from '@iconscout/react-unicons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from 'AuthConfigure';
import '../../../base/StandardTab/StandardTab.scss';

const SignInForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const [showError, setShowError] = useState(false);
  const [FormData, setFormData] = useState<LoginFormI>({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loginType, setLoginType] = useState<LogInType>(LogInType.MICROSOFT);
  const { instance } = useMsal();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isValidForm: boolean = validationFunctions.checkFormValidity(
      FormData,
      LoginValidations
    );
    try {
      if (!isValidForm) {
        setShowError(true);
        return;
      } else {
        setShowError(false);
        setIsLoading(true);
        const response = await dispatch(authActions.login(FormData));

        if (response.status === 201) {
          successNotification(response.data?.message);
          clearCookie(ACCESS_TOKEN);
          clearCookie(ROLE);
          clearCookie('AuthToken');
          clearCookie('i_user_id');

          setCookie(ROLE, response.data?.role?.role_name);
          setCookie(ACCESS_TOKEN, response?.data?.token);
          await dispatch(getProfile());
          navigate(
            redirectUrl && redirectUrl.startsWith('/') ? redirectUrl : USER
          );
          setIsLoading(false);
        } else {
          errorNotification(response?.data?.message);
          setIsLoading(false);
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    try {
      console.log(loginRequest);
      const authResult = await instance.loginPopup(loginRequest);
      if (authResult?.account) {
        handleStoreTokenInSystem(
          authResult?.accessToken,
          authResult?.account?.username?.toLocaleLowerCase()?.trim(),
          authResult?.idToken
        );
        setIsLoading(false);
      } else {
        errorNotification(
          capitalizeFirstLetter(t('login_signup.microsoft.login_failed'))
        );
        instance.clearCache();
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      instance.clearCache();
      console.log(error);
    }
  };

  const handleStoreTokenInSystem = async (
    token: string,
    email: string,
    idToken: string
  ) => {
    setIsLoading(true);

    try {
      const response = await dispatch(
        authActions.loginWithMicrosoft({
          token,
          email,
          idToken
        })
      );

      if (response.status === 201 && response.data?.token) {
        const { id, token: accessToken, message } = response.data;

        clearCookie(ACCESS_TOKEN);
        clearCookie(ROLE);
        clearCookie('AuthToken');
        clearCookie('i_user_id');

        setCookie(ROLE, response.data?.role?.role_name);
        setCookie(ACCESS_TOKEN, response?.data?.token);
        await dispatch(getProfile());
        navigate(
          redirectUrl && redirectUrl.startsWith('/') ? redirectUrl : USER
        );
        setIsLoading(false);
      } else {
        errorNotification(
          response.data?.message ||
            capitalizeFirstLetter(t('login_signup.microsoft.login_failed'))
        );
        instance.clearCache();
        setIsLoading(false);
      }
    } catch (error) {
      instance.logoutRedirect({
        onRedirectNavigate: () => false
      });
      instance.clearCache();
      setIsLoading(false);
    }
  };

  const handleOnChange = (value: string | number, name: keyof LoginFormI) => {
    setFormData({ ...FormData, [name]: value });
  };
  return (
    <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
      {/* <LanguageChange /> */}

      <Form onSubmit={handleLogin}>
        <Tab.Container
          activeKey={loginType}
          onSelect={e => setLoginType(e as LogInType)}
        >
          <div className="text-center mb-7">
            <h3 className="text-body-highlight">
              {capitalizeFirstLetter(t('login_signup.button.login'))}
            </h3>
            <p className="text-body-tertiary">
              {t('login_signup.general.subtitle')}
            </p>
          </div>
          <div className="text-center">
            <Nav variant="underline">
              <Nav.Item className="tab-nav-item">
                <Nav.Link eventKey={LogInType.MICROSOFT}>
                  {t('general.text_7')}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item className="tab-nav-item">
                <Nav.Link eventKey={LogInType.CRED}>
                  {t('general.text_8')}
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </div>

          <Tab.Content>
            <Tab.Pane eventKey={LogInType.MICROSOFT}>
              <div style={{ height: '207px' }}>
                <div
                  className="bg-danger text-white py-1   px-3 text-center rounded-xl mt-3 d-flex align-items-center justify-content-center gap-3 cursor-pointer"
                  onClick={() => handleMicrosoftLogin()}
                >
                  {t('general.text_11')}
                  <UilMicrosoft />
                </div>
              </div>
            </Tab.Pane>
            <Tab.Pane eventKey={LogInType.CRED}>
              <div className="mt-3">
                <StandardInput
                  className=""
                  label={t('general.common_text.text_4')}
                  placeholder={t('general.common_text.text_4')}
                  type="email"
                  value={FormData.email}
                  onChange={value => handleOnChange(value, 'email')}
                  validations={LoginValidations.email}
                  showError={showError}
                  icon={
                    <FontAwesomeIcon
                      icon={faUser}
                      className="text-body fs-9 form-icon"
                    />
                  }
                  inputClassName="form-icon-input"
                />
              </div>
              <StandardInput
                className=""
                label={t('general.common_text.text_3')}
                placeholder={t('general.common_text.text_3')}
                type="password"
                value={FormData.password}
                onChange={value => handleOnChange(value, 'password')}
                validations={LoginValidations.password}
                showError={showError}
                icon={
                  <FontAwesomeIcon
                    icon={faKey}
                    className="text-body fs-9 form-icon"
                  />
                }
                inputClassName="form-icon-input"
              />
              <Button variant="danger" className="w-100 mb-3" type="submit">
                {t('login_signup.button.login')}
              </Button>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Form>
    </ConditionalRender>
  );
};

export default SignInForm;
